// react, store
import React, {useState, useEffect, FC} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import actions from 'store/actions';
import moment from 'moment';
import settings from 'settings/site';
//type, enums
import {FacilityKey, LocalStorageEnum} from 'enums';
import {Dictionary} from 'dictionary';
import {PublicEventType} from 'enums/public_event_type';
// components
import DateFilter from 'routes/classes/DateFilter';
import {PublicSchedule} from './PublicSchedule';
import FacilityFilter from 'routes/classes/FacilityFilter';
import {NonMember} from './nonMember';
import IStoreState from 'store/types';
import {useNavigate} from 'react-router-dom';

interface IProps {
  memberId?: string;
}

export enum SeminarViewEnum {
  MEMBER = 'MEBMER',
  NON_MEBMER = 'NON_MEBMER',
}

export interface ModalHandlerProps {
  type: PublicEventType;
  eventId: string;
  eventTitle: string;
  date: string;
  hasMemberPrice: boolean;
  seminarView?: SeminarViewEnum;
}

const type = PublicEventType.SEMINAR;

export const PublicEvents: FC<IProps> = ({memberId}: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const publicEvents = useSelector((state: IStoreState) => state.publicEvents.list ?? []);

  const publicEventId = localStorage.getItem(LocalStorageEnum.PUBLIC_EVENT_ID);
  const publicEventDate = localStorage.getItem(LocalStorageEnum.PUBLIC_EVENT_DATE);

  const [facility, setFacility] = useState<string>(FacilityKey.CQ);
  const [date, setDate] = useState<string>(moment().utcOffset(settings.timeZoneOffset).format('YYYY-MM-DD'));
  const [eventDate, setEventDate] = useState(publicEventDate || undefined);
  const [eventIdToBook, setEventIdToBook] = useState(publicEventId ?? '');
  const [currEventType, setCurrEventType] = useState<PublicEventType>();
  const [seminarView, setSeminarView] = useState<SeminarViewEnum>();
  const [eventIdProcessing, setEventIdProcessing] = useState<string | undefined>();

  useEffect(() => {
    return () => {
      dispatch(actions.status.status());
    };
  }, [dispatch]);

  useEffect(() => {
    actions.publicEvents.getList({date, facility, type, memberId}, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, facility, memberId, type]);

  useEffect(() => {
    if (publicEventId && publicEventDate) {
      const publicEvent = publicEvents.find(({event}) => event.id === publicEventId);
      if (publicEvent) {
        onModalHandler({
          type: publicEvent.event.type,
          eventId: publicEventId,
          eventTitle: publicEvent.title,
          date: publicEventDate,
          hasMemberPrice: !!publicEvent?.event.memberPrice,
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function bookSeminarByMember(props: ModalHandlerProps) {
    dispatch(actions.status.status());
    const {eventId, date, type, hasMemberPrice, eventTitle} = props;

    if (!memberId) {
      localStorage.setItem(LocalStorageEnum.PUBLIC_EVENT_ID, eventId);
      localStorage.setItem(LocalStorageEnum.PUBLIC_EVENT_DATE, date);

      setEventIdToBook('');
      setEventDate(undefined);
      setSeminarView(undefined);
      navigate('/login');
    } else {
      setEventIdProcessing(eventId);
      try {
        await actions.publicEvents.book(
          {eventId, eventTitle: eventTitle, memberId, date, action: 'book', eventType: type, hasPrice: hasMemberPrice},
          dispatch
        );
      } finally {
        setEventIdProcessing(undefined);
      }
    }
  }

  const onCancel = () => {
    setEventIdToBook('');
    setEventDate(undefined);
    setSeminarView(undefined);
    localStorage.removeItem(LocalStorageEnum.PUBLIC_EVENT_ID);
    localStorage.removeItem(LocalStorageEnum.PUBLIC_EVENT_DATE);
  };

  const onModalHandler = (props: ModalHandlerProps) => {
    setEventDate(props.date);
    setEventIdToBook(props.eventId);
    setCurrEventType(props.type);
    setSeminarView(props.seminarView);

    if (props.seminarView === SeminarViewEnum.MEMBER) {
      bookSeminarByMember(props);
    }
  };

  const isSeminarNonMember = seminarView === SeminarViewEnum.NON_MEBMER && currEventType === PublicEventType.SEMINAR;
  const showModal = eventIdToBook && eventDate && currEventType && isSeminarNonMember;

  return (
    <div className="body">
      <div className="d-flex justify-content-center">
        <h2>Public Events</h2>
      </div>

      <section>
        <FacilityFilter facilities={Dictionary.facilities} facility={facility} setFacility={setFacility} />
        <DateFilter date={date} setDate={setDate} />
      </section>

      <PublicSchedule {...{date, onModalHandler}} eventIdProcessing={eventIdProcessing} />

      {showModal && <NonMember {...{onCancel, currEventType, eventIdToBook, eventDate, facility}} />}
    </div>
  );
};
